import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout as AntLayout,
  Menu,
  Button,
  Dropdown,
  Avatar,
  Typography,
  Space,
  Badge,
  Tooltip,
} from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  SettingOutlined,
  SearchOutlined,
  ExportOutlined,
  UserOutlined,
  LogoutOutlined,
  BellOutlined,
  EyeOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  FileTextOutlined,
  HomeOutlined,
  TeamOutlined,
  ApartmentOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  CloudOutlined,
  ToolOutlined,
  AppstoreOutlined,
  MenuOutlined,
  ProfileOutlined,
  BookOutlined,
  FileSearchOutlined,
  FileOutlined,
  FolderOutlined,
  ClockCircleOutlined,
  FundOutlined,
  WalletOutlined,
  ApiOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store';
import { logoutUser } from '../store/slices/authSlice';
import { toggleSidebar, updateSettings } from '../store/slices/settingsSlice';
import { staticMenuConfig, StaticMenuItem } from '../constants/menu';
import { getCurrentMenuTree, type Menu as BackendMenu } from '../services/menuService';
import { applySystemSettingsBrand, getSystemSettings } from '../services/systemSettingsService';
import { normalizeFileUrl } from '../services/fileService';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

// 图标映射
const iconMap: Record<string, React.ReactNode> = {
  DashboardOutlined: <DashboardOutlined />,
  SearchOutlined: <SearchOutlined />,
  EyeOutlined: <EyeOutlined />,
  HeartOutlined: <HeartOutlined />,
  MedicineBoxOutlined: <MedicineBoxOutlined />,
  ExportOutlined: <ExportOutlined />,
  SettingOutlined: <SettingOutlined />,
  UserOutlined: <UserOutlined />,
  TeamOutlined: <TeamOutlined />,
  HomeOutlined: <HomeOutlined />,
  ApartmentOutlined: <ApartmentOutlined />,
  FileTextOutlined: <FileTextOutlined />,
  BellOutlined: <BellOutlined />,
  SafetyOutlined: <SafetyOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
  CloudOutlined: <CloudOutlined />,
  ToolOutlined: <ToolOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  MenuOutlined: <MenuOutlined />,
  ProfileOutlined: <ProfileOutlined />,
  BookOutlined: <BookOutlined />,
  FileSearchOutlined: <FileSearchOutlined />,
  FileOutlined: <FileOutlined />,
  FolderOutlined: <FolderOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  FundOutlined: <FundOutlined />,
  WalletOutlined: <WalletOutlined />,
  ApiOutlined: <ApiOutlined />,
  ShopOutlined: <ShopOutlined />,
};

const convertToAntMenuItems = (menus: StaticMenuItem[]): any[] => {
  return menus.map((menu) => ({
    key: menu.path || menu.key,
    icon: menu.icon ? iconMap[menu.icon] : undefined,
    label: menu.label,
    children: menu.children?.length ? convertToAntMenuItems(menu.children) : undefined,
  }));
};

const convertBackendMenusToAntMenuItems = (menus: BackendMenu[]): any[] => {
  // Button permissions participate in authorization but are not navigation
  // entries. Rendering them here would leave a clickable sidebar item without
  // a route.
  return menus.filter(menu => menu.type !== 3).map((menu) => ({
    key: menu.path || menu.id,
    icon: menu.icon ? iconMap[menu.icon] : undefined,
    label: menu.name,
    children: menu.children?.length ? convertBackendMenusToAntMenuItems(menu.children) : undefined,
  }));
};

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { sidebarCollapsed, settings } = useAppSelector((state) => state.settings);
  const [backendMenus, setBackendMenus] = useState<BackendMenu[]>([]);
  const [menusLoaded, setMenusLoaded] = useState(false);

  const menuItems = useMemo(() => {
    if (menusLoaded) {
      return convertBackendMenusToAntMenuItems(backendMenus);
    }
    return convertToAntMenuItems(staticMenuConfig);
  }, [backendMenus, menusLoaded]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key.startsWith('/')) {
      navigate(key);
    }
  };

  // 用户下拉菜单
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '个人设置',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  const selectedMenuPath = location.pathname.startsWith('/finance/report/')
    ? '/finance/report/overview'
    : location.pathname;

  // 获取当前选中的菜单项
  const selectedKeys = [selectedMenuPath];

  // 获取展开的菜单项
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  useEffect(() => {
    const loadSystemSettings = async () => {
      try {
        const remoteSettings = await getSystemSettings();
        dispatch(updateSettings(remoteSettings));
        applySystemSettingsBrand(remoteSettings);
      } catch {
        // 系统设置加载失败不影响后台主体功能。
      }
    };

    void loadSystemSettings();
  }, [dispatch]);

  useEffect(() => {
    const loadMenus = async () => {
      try {
        const response = await getCurrentMenuTree();
        setBackendMenus(response.data || []);
        setMenusLoaded(true);
      } catch {
        setBackendMenus([]);
        setMenusLoaded(false);
      }
    };

    void loadMenus();
  }, [user?.id]);

  // 当路径变化时，计算应展开的父级菜单
  useEffect(() => {
    if (menuItems.length === 0) {
      return;
    }

    const path = selectedMenuPath;

    const findParent = (menus: any[], targetPath: string, parents: string[] = []): string[] | null => {
      for (const menu of menus) {
        if (menu.key === targetPath) {
          return parents;
        }
        if (menu.children) {
          const result = findParent(menu.children, targetPath, [...parents, menu.key]);
          if (result) return result;
        }
      }
      return null;
    };

    const parents = findParent(menuItems, path) || [];
    setOpenKeys((prev) => {
      if (prev.length === parents.length && prev.every((key, index) => key === parents[index])) {
        return prev;
      }
      return parents;
    });
  }, [menuItems, selectedMenuPath]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  return (
    <AntLayout className="layout-container">
      <Header
        className="layout-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            type="text"
            icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => dispatch(toggleSidebar())}
            style={{ marginRight: 16 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {settings.logo ? (
              <img src={normalizeFileUrl(settings.logo)} alt="logo" style={{ width: 22, height: 22, objectFit: 'contain' }} />
            ) : (
              <EyeOutlined style={{ fontSize: 20, color: '#1890ff' }} />
            )}
            <Text strong style={{ fontSize: 18, color: '#1890ff' }}>
              {settings.siteName || '悠米宝贝宠物管理平台'}
            </Text>
          </div>
        </div>

        <Space size="large">
          <Tooltip title="通知">
            <Badge count={0}>
              <Button
                type="text"
                icon={<BellOutlined style={{ fontSize: 16 }} />}
                onClick={() => navigate('/notifications')}
              />
            </Badge>
          </Tooltip>

          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            arrow
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                style={{ backgroundColor: '#1890ff' }}
              />
              <div style={{ textAlign: 'left', lineHeight: '1.2' }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: '2px' }}>
                  {user?.name}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>
                  {user?.organization}
                </div>
              </div>
            </div>
          </Dropdown>
        </Space>
      </Header>

      <AntLayout>
        <Sider
          className="layout-sider"
          width={240}
          collapsed={sidebarCollapsed}
          collapsedWidth={80}
          style={{
            background: '#fff',
            borderRight: '1px solid #f0f0f0',
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              borderRight: 'none',
            }}
          />
        </Sider>

        <Content className="layout-main">
          <div className="page-container">
            <Outlet />
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
